import type { Channel, Listing } from '../../reducers/types/reducerTypes'
import type {
  EnterPressHandler,
  FocusHandler,
} from '@noriginmedia/norigin-spatial-navigation'
import type { Dayjs } from 'dayjs'
import type { HoverHandler } from '../../hooks/useHybridFocusable/types'

export type SchedulesProps = {
  children: React.ReactNode
  timeIndicatorPosition: number
  hiddenListingAtStart: boolean
}

export type ScheduleProps = {
  data: Listing
  position: ListingPositionEnum
  onFocus: FocusHandler<{ channel: Channel; listing: Listing }>
  onHover: HoverHandler<{ channel: Channel; listing: Listing }>
  scheduleRange?: { start: Dayjs; end: Dayjs }
  channel: Channel
  hoursToDisplay: number
  focusKey: string
}
export type ChannelRow = {
  channel: Channel
  filteredListings: Array<{ listing: Listing; position: ListingPositionEnum }>
  hiddenListingAtStart?: Listing
  hiddenListingAtEnd?: Listing
}

export type ChannelRowProps = {
  data: Channel
  children: React.ReactNode
  onFocus: FocusHandler<{
    index: number
    channel: Channel
    focusedOnListingsGrid: boolean
  }>
  onHover: HoverHandler<{
    index: number
    channel: Channel
    focusedOnListingsGrid: boolean
  }>
  onEnterPress: EnterPressHandler<{ index: number; channel: Channel }>
  index: number
  visibility: 'visible' | 'hidden'
  hasFocusedChild: boolean
  hasHiddenListingAtStart: boolean
  handleHorizontalScrolling: (
    direction: 'left' | 'right',
    extraArgs: Record<string, unknown>
  ) => void
  canScrollLeft: boolean
  canScrollRight?: boolean
}

export type ChannelProps = {
  data: Channel
  hasFocusedChild: boolean
  index: number
  onFocus: FocusHandler<{
    index: number
    channel: Channel
    focusedOnListingsGrid: boolean
  }>
  onHover: HoverHandler<{
    index: number
    channel: Channel
    focusedOnListingsGrid: boolean
  }>
  onEnterPress: EnterPressHandler<{ index: number; channel: Channel }>
  hasHiddenListingAtStart: boolean
}

export type ChannelsProps = {
  children: (channelRow: ChannelRow, index: number) => React.ReactNode
  translateY: number
  channels: Channel[]
  baseTime: Dayjs
  scheduleRange?: { start: Dayjs; end: Dayjs }
  timeOffsetMinutes: number
  onLoad: () => void
}

export type EPGProps = {
  channels: Channel[]
  onChannelEnterPress?: (channel: Channel, playingListing: Listing) => void
  onChannelFocused?: (channel: Channel, focusedOnListingsGrid: boolean) => void
  onListingFocused?: (listing: Listing, focusedOnListingsGrid: boolean) => void
  className?: string
  stealFocus?: boolean
}

export type TimeLineProps = {
  currentTime: Dayjs
  focusedDate: Dayjs
  hoursToDisplay?: number
  slotDuration?: number
  onScheduleRangeChange?: (range: { start: Dayjs; end: Dayjs }) => void
  timeIndicatorPosition: number
}

export enum ListingPositionEnum {
  notInSchedule = 'notInSchedule',
  hiddenAtStart = 'hiddenAtStart',
  hiddenAtEnd = 'hiddenAtEnd',
  startsBeforeScheduleStartButNotEnded = 'startsBeforeScheduleStartButNotEnded',
  startsAtScheduleStart = 'startsAtScheduleStart',
  startsAtScheduleStartAndIsEnded = 'startsAtScheduleStartAndIsEnded',
  startsBeforeScheduleStartAndEnded = 'startsBeforeScheduleStartAndEnded',
  startsAfterScheduleEnd = 'startsAfterScheduleEnd',
  duringSchedule = 'duringSchedule',
  duringScheduleEndsAfterScheduleEnd = 'duringScheduleEndsAfterScheduleEnd',
  // admittedly, these are edge cases, but they're possible
  startsBeforeScheduleStartEndsAtScheduleEnd = 'startsBeforeScheduleStartEndsAtScheduleEnd',
  startsAtScheduleStartEndsAfterScheduleEnd = 'startsAtScheduleStartEndsAfterScheduleEnd',
  listingSameAsSchedule = 'listingSameAsSchedule',
  listingLongerThanSchedule = 'listingLongerThanSchedule',
}
